import React, { useRef } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    Button,
    Typography,
    Box,
    CircularProgress,
} from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTriangleExclamation } from '@fortawesome/free-solid-svg-icons';

interface ConfirmationDialogProps {
    open: boolean;
    title: string;
    message: string;
    confirmLabel?: string;
    cancelLabel?: string;
    isDestructive?: boolean;
    /** When destructive: focus Cancel first (default). Set false to keep primary confirm focused. */
    focusCancelOnDestructive?: boolean;
    isLoading?: boolean;
    onConfirm: () => void;
    onCancel: () => void;
    onSave?: () => void;
    testIdPrefix?: string;
    cancelTestId?: string;
    confirmTestId?: string;
    saveTestId?: string;
}

const ConfirmationDialog: React.FC<ConfirmationDialogProps> = ({
    open,
    title,
    message,
    confirmLabel = 'Confirm',
    cancelLabel = 'Cancel',
    isDestructive = false,
    focusCancelOnDestructive,
    isLoading = false,
    onConfirm,
    onCancel,
    onSave,
    testIdPrefix,
    cancelTestId,
    confirmTestId,
    saveTestId,
}) => {
    const cancelFocusedFirst = isDestructive ? focusCancelOnDestructive !== false : false;
    const cancelRef = useRef<HTMLButtonElement | null>(null);
    return (
        <Dialog
            open={open}
            onClose={isLoading ? undefined : onCancel}
            slotProps={{
                transition: {
                    // A dialog opened over the drawer: focus Cancel once the dialog has
                    // settled, so the destructive choice is never the default.
                    onEntered: () => {
                        if (cancelFocusedFirst) cancelRef.current?.focus();
                    },
                },
            }}
            PaperProps={{
                sx: {
                    borderRadius: '1.5rem',
                    padding: '1rem',
                    bgcolor: 'var(--color-bg-secondary)',
                    border: '1px solid var(--color-border)',
                    boxShadow: 'var(--shadow-card)',
                    // Room for Cancel, Discard, and Save and continue on one row.
                    maxWidth: onSave ? '460px' : '400px',
                },
            }}
        >
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 2, pb: 1 }}>
                {isDestructive && (
                    <Box sx={{ color: 'var(--color-danger)', display: 'flex', alignItems: 'center' }}>
                        <FontAwesomeIcon icon={faTriangleExclamation} />
                    </Box>
                )}
                <Typography component="span" variant="h6" sx={{ color: 'var(--color-text-primary)', fontWeight: 800, fontSize: '1.1rem' }}>
                    {title}
                </Typography>
            </DialogTitle>
            <DialogContent>
                <DialogContentText sx={{ color: 'var(--color-text-secondary)', fontSize: '0.95rem', lineHeight: 1.5 }}>
                    {message}
                </DialogContentText>
            </DialogContent>
            <DialogActions
                sx={onSave
                    ? {
                        px: 3,
                        pb: 2,
                        gap: 1,
                        flexWrap: 'nowrap',
                        // One row from sm up; one column on a phone, primary last.
                        flexDirection: { xs: 'column', sm: 'row' },
                        alignItems: { xs: 'stretch', sm: 'center' },
                        '& > :not(style) ~ :not(style)': { ml: 0 },
                    }
                    : { px: 3, pb: 2, gap: 1, flexWrap: 'wrap' }}
            >
                <Button
                    ref={cancelRef}
                    onClick={onCancel}
                    data-testid={cancelTestId ?? (testIdPrefix ? `${testIdPrefix}_CANCEL` : undefined)}
                    disabled={isLoading}
                    autoFocus={cancelFocusedFirst}
                    sx={{
                        color: 'var(--color-text-muted)',
                        textTransform: 'none',
                        fontWeight: 700,
                        '&:hover': { bgcolor: 'var(--color-bg-card-hover)' },
                    }}
                >
                    {cancelLabel}
                </Button>
                <Button
                    onClick={onConfirm}
                    data-testid={confirmTestId ?? (testIdPrefix ? `${testIdPrefix}_DISCARD` : undefined)}
                    variant={onSave ? 'outlined' : 'contained'}
                    disabled={isLoading}
                    autoFocus={!cancelFocusedFirst}
                    sx={{
                        bgcolor: onSave ? 'transparent' : isDestructive ? 'var(--color-danger)' : 'var(--color-accent)',
                        color: onSave ? 'var(--color-text-primary)' : isDestructive ? 'var(--color-on-danger)' : 'var(--color-on-accent)',
                        textTransform: 'none',
                        fontWeight: 700,
                        borderRadius: '12px',
                        px: onSave ? 2 : 3,
                        minWidth: onSave ? 0 : '100px',
                        '&:hover': onSave
                            ? { bgcolor: 'var(--color-bg-card-hover)' }
                            : isDestructive
                                ? { bgcolor: 'var(--color-danger)', filter: 'brightness(0.92)' }
                                : { bgcolor: 'var(--color-accent-hover)', color: 'var(--color-on-accent-hover)' },
                        '&.Mui-disabled': {
                            bgcolor: onSave ? 'transparent' : isDestructive ? 'var(--color-danger)' : 'var(--color-accent)',
                            color: onSave ? 'var(--color-text-primary)' : isDestructive ? 'var(--color-on-danger)' : 'var(--color-on-accent)',
                            opacity: 0.6,
                        }
                    }}
                >
                    {isLoading ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <CircularProgress size={16} color="inherit" />
                            <span>{confirmLabel}</span>
                        </Box>
                    ) : confirmLabel}
                </Button>
                {onSave && (
                    <Button
                        onClick={onSave}
                        data-testid={saveTestId ?? (testIdPrefix ? `${testIdPrefix}_SAVE` : undefined)}
                        variant="contained"
                        disabled={isLoading}
                        sx={{ textTransform: 'none', minHeight: 44, bgcolor: 'var(--color-accent-hover)', color: 'var(--color-bg-primary)', '&:hover': { bgcolor: 'var(--color-accent-hover)', filter: 'brightness(0.95)' } }}
                    >
                        {isLoading ? 'Saving…' : 'Save and continue'}
                    </Button>
                )}
            </DialogActions>
        </Dialog>
    );
};

export default ConfirmationDialog;
